export const vocabularyTiers = {
  A1: {
    level: 1,
    label: 'A1 (Basic)',
    description: 'Simple vocabulary, basic concepts, straightforward answers',
    color: 'bg-emerald-100 text-emerald-800',
    vocabularyLevel: 'Elementary',
    expectedAnswer: 'Simple facts, basic recall, one-word or short phrase responses',
    characteristics: {
      vocabulary: ['basic', 'simple', 'common', 'everyday', 'fundamental'],
      complexity: 'Very Low',
      cognitive: 'Minimal processing required',
      examples: ['What is...?', 'Name the...', 'List...', 'Choose...']
    },
    keywords: [
      'what', 'who', 'when', 'where', 'is', 'are', 'name', 'list', 'choose', 
      'select', 'identify', 'find', 'show', 'tell', 'basic', 'simple'
    ]
  },
  A2: {
    level: 2,
    label: 'A2 (Elementary)',
    description: 'Common vocabulary, simple explanations, basic understanding',
    color: 'bg-green-100 text-green-800',
    vocabularyLevel: 'Pre-intermediate',
    expectedAnswer: 'Simple explanations, basic connections, short paragraphs',
    characteristics: {
      vocabulary: ['common', 'straightforward', 'clear', 'standard', 'typical'],
      complexity: 'Low',
      cognitive: 'Basic understanding and simple connections',
      examples: ['Explain...', 'Describe...', 'How does...?', 'Why is...?']
    },
    keywords: [
      'explain', 'describe', 'how', 'why', 'because', 'show', 'demonstrate', 
      'simple', 'basic', 'main', 'important', 'different', 'same'
    ]
  },
  B1: {
    level: 3,
    label: 'B1 (Intermediate)',
    description: 'Academic vocabulary, detailed explanations, analytical thinking',
    color: 'bg-blue-100 text-blue-800',
    vocabularyLevel: 'Intermediate',
    expectedAnswer: 'Detailed explanations, logical reasoning, structured responses',
    characteristics: {
      vocabulary: ['academic', 'technical', 'specialized', 'precise', 'formal'],
      complexity: 'Medium',
      cognitive: 'Analysis, comparison, and logical reasoning',
      examples: ['Compare...', 'Analyze...', 'Discuss...', 'Examine...']
    },
    keywords: [
      'compare', 'contrast', 'analyze', 'examine', 'discuss', 'evaluate', 
      'consider', 'relationship', 'factors', 'aspects', 'implications'
    ]
  },
  B2: {
    level: 4,
    label: 'B2 (Upper-Intermediate)',
    description: 'Advanced vocabulary, complex analysis, sophisticated reasoning',
    color: 'bg-indigo-100 text-indigo-800',
    vocabularyLevel: 'Upper-intermediate',
    expectedAnswer: 'Complex analysis, multiple perspectives, well-structured arguments',
    characteristics: {
      vocabulary: ['sophisticated', 'nuanced', 'complex', 'advanced', 'intricate'],
      complexity: 'High',
      cognitive: 'Synthesis, evaluation, and critical thinking',
      examples: ['Critically evaluate...', 'Synthesize...', 'Justify...', 'Assess...']
    },
    keywords: [
      'critically', 'evaluate', 'assess', 'justify', 'synthesize', 'integrate', 
      'implications', 'consequences', 'perspectives', 'framework', 'paradigm'
    ]
  },
  C1: {
    level: 5,
    label: 'C1 (Advanced)',
    description: 'Expert vocabulary, theoretical concepts, original thinking',
    color: 'bg-purple-100 text-purple-800',
    vocabularyLevel: 'Advanced',
    expectedAnswer: 'Theoretical understanding, original insights, expert-level analysis',
    characteristics: {
      vocabulary: ['theoretical', 'conceptual', 'abstract', 'specialized', 'expert'],
      complexity: 'Very High',
      cognitive: 'Abstract thinking, theoretical application, innovation',
      examples: ['Theorize...', 'Conceptualize...', 'Formulate...', 'Propose...']
    },
    keywords: [
      'theorize', 'conceptualize', 'formulate', 'propose', 'hypothesize', 
      'paradigm', 'framework', 'methodology', 'theoretical', 'abstract'
    ]
  },
  C2: {
    level: 6,
    label: 'C2 (Expert)',
    description: 'Highly specialized vocabulary, original research, innovative solutions',
    color: 'bg-pink-100 text-pink-800',
    vocabularyLevel: 'Expert/Native',
    expectedAnswer: 'Original research, innovative solutions, expert-level insights',
    characteristics: {
      vocabulary: ['highly specialized', 'research-level', 'innovative', 'cutting-edge'],
      complexity: 'Expert',
      cognitive: 'Original research, innovation, expert judgment',
      examples: ['Innovate...', 'Pioneer...', 'Revolutionize...', 'Discover...']
    },
    keywords: [
      'innovate', 'pioneer', 'revolutionize', 'discover', 'breakthrough', 
      'cutting-edge', 'state-of-the-art', 'novel', 'unprecedented', 'groundbreaking'
    ]
  }
};

// Helper functions
export const getVocabularyTier = (tierKey) => {
  return vocabularyTiers[tierKey] || null;
};

export const getAllVocabularyTiers = () => {
  return Object.entries(vocabularyTiers).map(([key, value]) => ({ key, ...value }));
};

export const detectVocabularyTier = (questionText) => {
  const text = questionText.toLowerCase();
  const tiers = Object.entries(vocabularyTiers);
  
  // Score each tier based on keyword matches
  const tierScores = {};
  for (const [key, tier] of tiers) {
    let score = 0;
    for (const keyword of tier.keywords) {
      if (text.includes(keyword.toLowerCase())) {
        score += 1;
      }
    }
    tierScores[key] = score;
  }
  
  // Find tier with highest score
  const bestTier = Object.entries(tierScores).reduce((best, [tier, score]) => {
    return score > best.score ? { tier, score } : best;
  }, { tier: 'A1', score: 0 });
  
  return bestTier.tier;
};

export const getVocabularyComplexity = (questionText) => {
  const words = questionText.toLowerCase().split(/\s+/);
  const avgWordLength = words.reduce((sum, word) => sum + word.length, 0) / words.length;
  
  // Simple heuristic based on word length and question length
  if (avgWordLength < 4 && words.length < 10) return 'A1';
  if (avgWordLength < 5 && words.length < 15) return 'A2';
  if (avgWordLength < 6 && words.length < 25) return 'B1';
  if (avgWordLength < 7 && words.length < 35) return 'B2';
  if (avgWordLength < 8) return 'C1';
  return 'C2';
};

export const analyzeQuestionComplexity = (questionText, questionType, subject) => {
  const detectedTier = detectVocabularyTier(questionText);
  const complexityTier = getVocabularyComplexity(questionText);
  
  // Weight the analysis based on question type
  const typeWeights = {
    'Multiple Choice': 0.8,
    'True/False': 0.7,
    'Fill in the Blank': 0.9,
    'Short Answer': 1.0,
    'Essay': 1.3,
    'Problem Solving': 1.2
  };
  
  const weight = typeWeights[questionType] || 1.0;
  const tierLevels = { A1: 1, A2: 2, B1: 3, B2: 4, C1: 5, C2: 6 };
  const avgLevel = (tierLevels[detectedTier] + tierLevels[complexityTier]) / 2 * weight;
  const finalTiers = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
  const finalTierIndex = Math.min(Math.round(avgLevel) - 1, 5);
  
  return {
    tier: finalTiers[finalTierIndex],
    confidence: detectedTier === complexityTier ? 'high' : 'medium',
    factors: {
      keywordAnalysis: detectedTier,
      complexityAnalysis: complexityTier,
      typeWeight: weight
    }
  };
};